// src/components/review/IntakeDecisionCard.js
// ADR-032 Fase III — Decision Card (Grill Me v1). READ-ONLY.
// Renderiza os 7 blocos da view v_intake_decision_card para um run price_table.
// Espelha o idioma visual de ErelEntityCard (Tailwind slate/blue + lucide-react),
// adaptado para read-only: NENHUMA ação que escreva (sem .rpc/.insert/.update/.delete).
// A ação governada de aplicar (fn_launches_apply) chega na Fase III-B.

import React from 'react';
import {
  FileSpreadsheet, AlertTriangle, Layers, Lightbulb,
  ShieldAlert, ClipboardList, Flag,
} from 'lucide-react';

const ROUTE_TONE = {
  review: 'bg-violet-100 text-violet-800 border-violet-200',
  gap: 'bg-amber-100 text-amber-800 border-amber-200',
};

// Linha "aplicado / pendente" — destaca o pendente em âmbar quando > 0.
function StatRow({ label, aplicado, pendente }) {
  const pend = pendente ?? 0;
  return (
    <div className="flex items-baseline justify-between gap-2 text-[11px]">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono">
        <span className="text-emerald-700 font-semibold">{aplicado ?? 0}</span>
        <span className="text-slate-300"> / </span>
        <span className={pend > 0 ? 'text-amber-700 font-bold' : 'text-slate-400'}>{pend}</span>
      </span>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="border-t border-slate-100 pt-2.5">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
        {Icon && <Icon size={12} className="text-slate-400" />} {title}
      </div>
      {children}
    </div>
  );
}

// Chip de estado read-only (sem clique). Verde quando ativo, slate quando não.
function StateChip({ on, labelOn, labelOff }) {
  return (
    <span
      className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${
        on
          ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
          : 'bg-slate-100 text-slate-500 border-slate-200'
      }`}
    >
      {on ? labelOn : labelOff}
    </span>
  );
}

export default function IntakeDecisionCard({ card }) {
  const {
    run_id, file_name, pipeline_status, input_type, detected_workflow, route,
    bloco_sabe, bloco_falta, bloco_recomendacao, bloco_evidencia,
    bloco_risco, bloco_decisao, bloco_gap,
  } = card || {};

  const vazio = (bloco_evidencia?.actions_total ?? 0) === 0;
  const aviso = bloco_evidencia?.aviso || null;
  const gaps = Array.isArray(bloco_gap) ? bloco_gap : [];

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
      {/* HEADER */}
      <div className="flex items-start gap-3">
        <FileSpreadsheet size={18} className="text-violet-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div
            className="text-base font-bold text-slate-900 leading-snug truncate"
            title={file_name || run_id}
          >
            {file_name || '(sem nome de arquivo)'}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${
                ROUTE_TONE[route] || 'bg-slate-100 text-slate-700 border-slate-200'
              }`}
              title="Roteamento do Workflow Router (ADR-032)"
            >
              route: {route || '—'}
            </span>
            {input_type && (
              <span className="text-[11px] text-slate-500 font-mono">{input_type}</span>
            )}
            {detected_workflow && (
              <>
                <span className="text-[11px] text-slate-300">→</span>
                <span className="text-[11px] text-slate-600 font-mono">{detected_workflow}</span>
              </>
            )}
            <span className="text-[11px] text-slate-300">·</span>
            <span className="text-[11px] text-slate-500">{pipeline_status || '—'}</span>
            {vazio && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-500 border border-slate-200"
                title="Run sem ações de intake (ex.: failed) — cobertura parcial explícita (D-09)"
              >
                sem ações
              </span>
            )}
          </div>
        </div>
      </div>

      {/* BLOCO SABE / FALTA (aplicado / pendente) */}
      <Section icon={Layers} title="Estrutura & preço — aplicado / pendente">
        <div className="space-y-1">
          <StatRow
            label="Empreendimentos"
            aplicado={bloco_sabe?.empreendimentos_aplicados}
            pendente={bloco_falta?.empreendimentos_pendentes}
          />
          <StatRow
            label="Tipologias"
            aplicado={bloco_sabe?.tipologias_aplicadas}
            pendente={bloco_falta?.tipologias_pendentes}
          />
          <StatRow
            label="Tabelas de preço"
            aplicado={bloco_sabe?.price_sets_aplicados}
            pendente={bloco_falta?.price_sets_pendentes}
          />
          <div className="border-t border-slate-100 pt-1">
            <StatRow
              label="Total de ações"
              aplicado={bloco_sabe?.actions_aplicadas_total}
              pendente={bloco_falta?.actions_pendentes_total}
            />
          </div>
        </div>
      </Section>

      {/* BLOCO RECOMENDAÇÃO */}
      <Section icon={Lightbulb} title="Recomendação">
        <ul className="space-y-1 text-[11px] text-slate-700">
          <li><span className="text-slate-400">estrutura:</span> {bloco_recomendacao?.estrutura || '—'}</li>
          <li><span className="text-slate-400">preço:</span> {bloco_recomendacao?.preco || '—'}</li>
          <li><span className="text-slate-400">preflight:</span> {bloco_recomendacao?.preflight_global || '—'}</li>
        </ul>
      </Section>

      {/* BLOCO EVIDÊNCIA */}
      <Section icon={ClipboardList} title="Evidência">
        <div className="text-[11px] text-slate-700">
          <span className="font-mono font-semibold">{bloco_evidencia?.actions_com_evidencia ?? 0}</span>
          <span className="text-slate-400"> / </span>
          <span className="font-mono">{bloco_evidencia?.actions_total ?? 0}</span>
          <span className="text-slate-500"> ações com evidência textual</span>
        </div>
        {aviso && (
          <div className="mt-1.5 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
            <span>{aviso}</span>
          </div>
        )}
      </Section>

      {/* BLOCO RISCO */}
      <Section icon={ShieldAlert} title="Risco">
        <ul className="space-y-1 text-[11px] text-slate-700">
          <li><span className="text-slate-400">estrutural:</span> {bloco_risco?.estrutural || '—'}</li>
          <li><span className="text-slate-400">preço:</span> {bloco_risco?.preco || '—'}</li>
        </ul>
      </Section>

      {/* BLOCO DECISÃO (read-only — ação chega na Fase III-B) */}
      <Section icon={Flag} title="Decisão">
        <div className="flex items-center gap-2 flex-wrap">
          <StateChip
            on={!!bloco_decisao?.aplicar_estrutura}
            labelOn="aplicar estrutura: sim"
            labelOff="aplicar estrutura: não"
          />
          <StateChip
            on={!!bloco_decisao?.habilitar_preco}
            labelOn="habilitar preço: sim"
            labelOff="habilitar preço: não"
          />
        </div>
        <p className="mt-1.5 text-[10px] text-slate-400 italic">
          Visualização read-only — a ação governada de aplicar chega na Fase III-B.
        </p>
      </Section>

      {/* BLOCO GAP */}
      {gaps.length > 0 && (
        <Section icon={AlertTriangle} title={`Lacunas (${gaps.length})`}>
          <ul className="space-y-1">
            {gaps.map((g, i) => (
              <li key={`${g?.gap_tipo || 'gap'}-${i}`} className="text-[11px] text-slate-700">
                <span className="font-mono text-amber-700">{g?.gap_tipo || '—'}</span>
                <span className="text-slate-400"> · {g?.status || '—'}</span>
                {g?.descricao && <div className="text-slate-500">{g.descricao}</div>}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
